import { executeRequest } from "@/common/utils/http/httpClient";
import { getCurrentEnvironmentVariables, constants } from "@/lib/CONSTANTS";
import { endpointMapper } from "@/common/service/endpoint.map.js";
import { replaceAllMatchPattern, queryParamGenerator } from "@/common/utils/generic/commonHelper.js";

const envSpecificApiDomain = getCurrentEnvironmentVariables().apiDomain;
const newSubmissionFlowEnabledCountryManifests =
  constants.pageSettings.manifest.newSubmissionFlowEnabledCountryManifests;

const subPath = "v1";
//const subPathV2 = "v2";

// Service Paths urls
const portCallApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].portCall)}`;
const complianceApiPath = `${replaceAllMatchPattern(
  envSpecificApiDomain[subPath],
  endpointMapper[subPath].compliance
)}`;
const myPageApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].myPage)}`;
const vesselScheduleApiPath = `${replaceAllMatchPattern(
  envSpecificApiDomain[subPath],
  endpointMapper[subPath].vesselSchedule
)}`;

// ------------------------------------- Helper Functions --------------------------------------------------

/**
 * Helper function to build notification parameters from customNotificationStatus array
 * @param {Array} customNotificationStatus - Array of notification status objects
 * @returns {string} - URL-encoded notification parameters string
 */
const buildNotificationParams = customNotificationStatus => {
  if (!Array.isArray(customNotificationStatus) || customNotificationStatus.length === 0) {
    return "";
  }

  return customNotificationStatus
    .map(item => {
      if (item.category && Array.isArray(item.values) && item.values.length > 0) {
        // Properly encode each value individually, then join with commas
        const encodedValues = item.values.map(value => encodeURIComponent(value)).join(",");
        return `notification.${encodeURIComponent(item.category)}=${encodedValues}`;
      }
      return "";
    })
    .filter(param => param !== "")
    .join("&");
};

/**
 * Helper function to safely append query parameters to a URL
 * @param {string} baseUrl - The base URL
 * @param {string} params - Additional parameters to append
 * @returns {string} - URL with safely appended parameters
 */
const appendQueryParams = (baseUrl, params) => {
  if (!params) {
    return baseUrl;
  }

  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}${params}`;
};

// ------------------------------------- Manifest Service --------------------------------------------------
/**
 * getManifestDetails
 * @description: Function which will trigger async call to get respective compliance detail
 *              based on queryParams to trigger this API
 * @returns {Promise} Array of vessel object
 * @param {Object} queryParams - Query parameters for the manifest details request
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */

const getManifestDetails = async (queryParams, rawResponse = false) => {
  const {
    compId,
    compPort,
    clearanceLocation = "",
    page,
    size,
    loadPort,
    complianceType,
    statusValue,
    stageValue,
    customsStatus = "",
    customNotificationStatus = [],
    bolType = "",
    cargoType = "",
    moveType = "",
    okToLoadValue,
    bolNumber,
    consignmentType,
    isManuallyTriggered,
    sourceType,
    isBolLevelReferenceExists,
    bda,
    applicableReferenceTypes,
    placeOfDelivery,
    sourcePage,
    showExemptionsOnly = false,
    exemptionType = null
  } = queryParams;

  const notificationParams = buildNotificationParams(customNotificationStatus);

  // Handle Empty Bill filter - extract isEmpty from bolType if present
  let filteredBolType = bolType;
  let isEmpty = "";
  if (bolType && bolType.includes("Empty Bill")) {
    isEmpty = "true";
    // Remove "Empty Bill" from bolType and keep other values
    filteredBolType = bolType
      .split(",")
      .filter(t => t.trim() !== "Empty Bill")
      .join(",");
  }

  let url;
  //TODO::This is the customized hardcoded values for the tempory purpose, we will make it dynamic once we are picking the main task
  if (sourceType == "compact") {
    let compactParams = `&isBolLevelReferenceExists=${isBolLevelReferenceExists}&bda=${bda}&applicableReferenceTypes=${applicableReferenceTypes}`;
    url =
      `${myPageApiPath}/aggregator/compact-view?page=${page}&size=${size}&complianceId=${compId}&compliancePort=${compPort}&loadPort=${loadPort}&customsStatus=${customsStatus}&bolType=${filteredBolType}&cargoType=${cargoType}&moveType=${moveType}&complianceType=${complianceType}&status=${statusValue}&stage=${stageValue}&bolNumber=${bolNumber}&consignmentType=${consignmentType}` +
      compactParams;
    url = appendQueryParams(url, notificationParams);
  } else {
    url = `${myPageApiPath}/aggregator/getBolDetailsByCompliance?page=${page}&size=${size}&complianceId=${compId}&compliancePort=${compPort}&loadPort=${loadPort}&customsStatus=${customsStatus}&bolType=${filteredBolType}&cargoType=${cargoType}&moveType=${moveType}&complianceType=${complianceType}&status=${statusValue}&stage=${stageValue}&bolNumber=${bolNumber}&consignmentType=${consignmentType}&isManuallyTriggered=${isManuallyTriggered}`;
    url = appendQueryParams(url, notificationParams);
  }
  // Add isEmpty filter if Empty Bill was selected
  if (isEmpty) {
    url = `${url}&isEmpty=${isEmpty}`;
  }
  if (newSubmissionFlowEnabledCountryManifests.includes(compId.substring(0, 5))) {
    url = url + `&sortBy=isEligibleForManualSubmission&sortOrder=desc`;
  }
  if (complianceType === "Import Rail Manifest") {
    url = `${url}&clearanceLocation=${clearanceLocation}`;
  }
  if (placeOfDelivery) {
    url = `${url}&deliveryCountryList=${placeOfDelivery}`;
  }
  if (okToLoadValue) {
    url = `${url}&okToLoad=${okToLoadValue}`;
  }
  if (showExemptionsOnly) {
    if (exemptionType === "ENS") {
      url = `${url}&isEligibleForENSExemption=true`;
    } else if (exemptionType === "SOC") {
      url = `${url}&isSocExempted=true`;
    } else {
      // Backward-compatible default if no explicit mode is provided
      url = `${url}&isEligibleForENSExemption=true&isSocExempted=true`;
    }
  }

  if (sourcePage == "BOL") {
    url = `${url}&searchType=BOL`;
  } else if (bolNumber) {
    url = `${url}&searchType=${consignmentType}`;
  }

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// ------------------------------------- Vessel Service --------------------------------------------------
/**
 * getVesselReferences
 * @description: Function which will trigger async call to get respective Vessel Reference detail
 *              based on queryParams to trigger this API
 * @returns {Promise} Array of vessel reference object
 * @param {Object} queryParams - Query parameters for the vessel reference request
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */

const getVesselReferences = async (queryParams, rawResponse = false) => {
  const { gsisKey } = queryParams;
  const url = `${portCallApiPath}/vesselReference?gsisKey=${gsisKey}&referenceName=weight`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getVesselNotificationDetails
 * @description: Function which will trigger async call to get respective Vessel Notification detail
 *              based on queryParams to trigger this API
 * @returns {Promise} Array of notification object
 * @param {Object} queryParams - Query parameters for the vessel notification request
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */

const getVesselNotificationDetails = async (queryParams, rawResponse = false) => {
  const { vesselCode, complianceType, compId } = queryParams;
  const url = `${myPageApiPath}/aggregator/vesselNotificationReferences?vesselCode=${vesselCode}&complianceType=${complianceType}&complianceId=${compId}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getManifestSearchData
 * @description: Function which will trigger async call to get respective page data
 *              based on the query Params to trigger this API
 * @returns {Promise} Array of manifest search data object
 * @param {Object} params - Parameters for the manifest search request
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */
const getManifestSearchData = async (params, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/searchManifest${queryParamGenerator(params)}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getManifestPageCount
 * @description: Function which will trigger async call to get respective count
 *              based on the compliance Id to trigger this API
 * @returns {Promise} Page count data
 * @param {Object} params - Parameters for the page count request
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */
const getManifestPageCount = async (params, rawResponse = false) => {
  const {
    compId,
    loadPort,
    clearanceLocation,
    complianceType,
    statusValue,
    customsStatus = "",
    customNotificationStatus = [],
    bolType = "",
    cargoType = "",
    moveType = "",
    stageValue,
    okToLoadValue,
    searchedConsignmentNumber,
    placeOfDelivery,
    showExemptionsOnly = false,
    exemptionType = null
  } = params;

  const notificationParams = buildNotificationParams(customNotificationStatus);

  // Handle Empty Bill filter - extract isEmpty from bolType if present
  let filteredBolType = bolType;
  let isEmpty = "";
  if (bolType && bolType.includes("Empty Bill")) {
    isEmpty = "true";
    // Remove "Empty Bill" from bolType and keep other values
    filteredBolType = bolType
      .split(",")
      .filter(t => t.trim() !== "Empty Bill")
      .join(",");
  }

  let url = `${myPageApiPath}/aggregator/consignmentReferenceDetail?complianceId=${compId}&loadPort=${loadPort}&complianceType=${complianceType}&customsStatus=${customsStatus}&bolType=${filteredBolType}&cargoType=${cargoType}&moveType=${moveType}&status=${statusValue}&stage=${stageValue}`;
  // Add isEmpty filter if Empty Bill was selected
  if (isEmpty) {
    url = `${url}&isEmpty=${isEmpty}`;
  }
  if (complianceType === "Import Rail Manifest") {
    url = `${url}&clearanceLocation=${clearanceLocation}`;
  }
  if (okToLoadValue) {
    url = `${url}&okToLoad=${okToLoadValue}`;
  }
  if (searchedConsignmentNumber) {
    url = `${url}&searchedConsignmentNumber=${searchedConsignmentNumber}`;
  }

  if (placeOfDelivery) {
    url = `${url}&deliveryCountryList=${placeOfDelivery}`;
  }

  if (showExemptionsOnly) {
    if (exemptionType === "ENS") {
      url = `${url}&isEligibleForENSExemption=true`;
    } else if (exemptionType === "SOC") {
      url = `${url}&isSocExempted=true`;
    } else {
      // Backward-compatible default if no explicit mode is provided
      url = `${url}&isEligibleForENSExemption=true&isSocExempted=true`;
    }
  }

  // Add notification parameters to the URL
  url = appendQueryParams(url, notificationParams);

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// ------------------------------------- Notification Service --------------------------------------------------
/**
 * getNotificationDetails
 * @description: Function which will trigger async call to get Notification Details
 *              based on the query Params to trigger this API
 * @returns Single String
 */
const getNotificationDetails = async (params, rawResponse = false) => {
  const { loadPort, voyageCode, vesselCode, compliancePort, complianceId, complianceType } = params;
  const url = `${myPageApiPath}/aggregator/manifest?complianceId=${complianceId}&complianceType=${complianceType}&compliancePort=${compliancePort}&vesselCode=${vesselCode}&voyageCode=${voyageCode}&loadPort=${loadPort}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getPreClearedCargoByBol
 * @description GET my-page: /aggregator/precleared-cargo/by-bol — Spring
 *              RequestParam bolNumbers as repeated query parameters (list of strings).
 * @param {string[]} bolNumbers
 * @param {boolean} rawResponse
 */
const getPreClearedCargoByBol = async (bolNumbers, rawResponse = false) => {
  const unique = [
    ...new Set(
      (bolNumbers || [])
        .map(b => String(b).trim())
        .filter(Boolean)
        .map(b => b.split("-")[0].trim())
        .filter(Boolean)
    )
  ];
  const search = new URLSearchParams();
  unique.forEach(b => search.append("bolNumbers", b));
  const qs = search.toString();
  const url = `${myPageApiPath}/aggregator/precleared-cargo/by-bol${qs ? `?${qs}` : ""}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// ------------------------------------- Manifest Submission & Update Service --------------------------------------------------
/**
 * updateSubmittedManifest
 * @description: Function which will trigger async call to submit/update manifest
 *              based on the complianceId to trigger this API
 * @returns {Promise} Submission result object
 * @param {Object} params - Parameters for the manifest submission
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */
const updateSubmittedManifest = async (params, rawResponse = false) => {
  const { complianceId, complianceType, loadPort, bolNumbers, isSelectedAllBol, body } = params;
  const url = `${complianceApiPath}/compliance/updateManualSubmissionFlag/${complianceId}?complianceType=${complianceType}&bolNumbers=${bolNumbers}&isSelectedAllBol=${isSelectedAllBol}&loadPort=${loadPort}`;

  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * cleardqeData
 * @description: Function which will trigger async call to clear DQE data
 * @returns {Promise} Clear operation result
 * @param {Object} params - Parameters for the clear DQE operation
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */
const cleardqeData = async (params, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/manualDQEClear`;

  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(params),
    rawResponse
  });
};

/**
 * getBrandDetails
 * @description: Function which will trigger async call to getBrand Details
 * @returns Array of bol data object
 * @param: {*}
 */
const getBrandDetails = async (params, rawResponse = false) => {
  const { complianceId, loadPort, consignmentType } = params;
  const url = `${complianceApiPath}/compliance/manifestSummary/${complianceId}?loadPort=${loadPort}&consignmentType=${consignmentType}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// ------------------------------------- Reference Service --------------------------------------------------
/**
 * getReferences
 * @description: Function which will trigger async call to get references details
 *              based on the bol number, complianceId and complianceType to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */
const getReferences = async (params, rawResponse = false) => {
  const { bolNumber, complianceId, complianceType, compliancePort, consignmentType, masterBolNumber } = params;
  const url = `${myPageApiPath}/aggregator/manifest/reference?bolNumber=${bolNumber}&complianceId=${complianceId}&complianceType=${complianceType}&compliancePort=${compliancePort}&consignmentType=${consignmentType}&masterBolNumber=${masterBolNumber}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * updateCustomReferences
 * @description: Function which will trigger async call to update custom Refrences
 * @returns Array of bol data object
 * @param: {*}
 */
const updateCustomReferences = async (body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/customReference`;

  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * deleteReferences
 * @description: Function which will trigger async call to delete Refrences
 * @returns
 * @param: refId, level, type, mandatory, amendment
 */
const deleteReferences = async (params, rawResponse = false) => {
  const {
    refId,
    level,
    type,
    mandatory,
    amendment,
    complianceId,
    bolNumber,
    id,
    value,
    firstEventLinkedDate,
    userName,
    userId
  } = params;
  const url = `${myPageApiPath}/aggregator/customReference?refId=${refId}&level=${level}&type=${type}&id=${id}&mandatory=${mandatory}&amendment=${amendment}&complianceId=${complianceId}&value=${value}&bolNumber=${bolNumber}&firstEventLinkedDate=${firstEventLinkedDate}&userName=${userName}&userId=${userId}`;

  return executeRequest({
    url,
    method: "DELETE",
    rawResponse
  });
};

/**
 * updateVesselCertificateDetails
 * @description:Function which will trigger async call to the data for updating vessel Certificate
 * @returns :A
 * @body : req_body as input which contains references object
 **/

const updateVesselCertificateDetails = async (body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/admin/vesselCertificates/`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

// ------------------------------------- Compliance & Approval Service --------------------------------------------------

/**
 * submitManualApproval
 * @description: Function which will trigger async call to update Notification status
 *              based on the query Params to trigger this API
 * @returns Object
 */
const submitManualApproval = async (params, rawResponse = false) => {
  const { complianceId, status, enableManualApprovalOnRejection, enableBolStatusValidation } = params;
  const requestBody = {
    status,
    enableManualApprovalOnRejection,
    enableBolStatusValidation
  };
  const url = `${complianceApiPath}/compliance/${complianceId}/status`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(requestBody),
    rawResponse
  });
};

// ------------------------------------- Utility Service --------------------------------------------------

/**
 * unlinkManifestAndConsignment
 * @description: Function which will trigger async call to unlink manifest and consignment
 * @returns Array of bol data object
 * @param: {*}
 */
const unlinkManifestAndConsignment = async (body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/manual/unlink`;

  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(body),
    rawResponse
  });
};

const updateFrozenETA = async (params, rawResponse = false) => {
  const { headers, body } = params;
  const url = `${vesselScheduleApiPath}/portcall/update-frozen-eta`;

  return executeRequest({
    url,
    headers,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * updateStageAndStatus
 * @description: Function which will trigger async call to update stage and status
 * @returns {Promise} Response from the update operation
 * @param {Object} body - Request body containing complianceID, stage, status, and updatedBy
 * @param {boolean} rawResponse - Whether to return raw response or processed response
 */
const updateStageAndStatus = async (body, rawResponse = false) => {
  const url = `${complianceApiPath}/compliance/vesselReferences/stageAndStatus`;

  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

export {
  getManifestDetails,
  getVesselReferences,
  getVesselNotificationDetails,
  getManifestSearchData,
  getManifestPageCount,
  getNotificationDetails,
  getPreClearedCargoByBol,
  cleardqeData,
  updateSubmittedManifest,
  getBrandDetails,
  getReferences,
  updateCustomReferences,
  deleteReferences,
  updateVesselCertificateDetails,
  submitManualApproval,
  unlinkManifestAndConsignment,
  updateFrozenETA,
  updateStageAndStatus
};
